import React, { useEffect } from "react";
import {
  RedButton,
  CustomLabel,
  FlexContainer,
  Input,
  ModalComponent,
  withRouter,
  ToastService,
  UtilityMethods,
  Loader,
} from "@mindmemobile/mm-shared-global";
import { observer } from "mobx-react";
import { NavigateFunction } from "react-router-dom";
import { AuthService } from "../../../shared/services";
import { PromotionModel } from "../../../shared/models/PromotionModels";
import PromotionService from "../../../shared/services/PromotionService";
import { firstValueFrom } from "rxjs";

type IProps = {
  navigate: NavigateFunction;
  promotionId: string;
  onClose: (refresh: boolean) => void;
};

const DeletePromotionPopUp: React.FC<IProps> = ({ onClose, promotionId }) => {
  const [isAPIRequestPending, setAPIRequestPending] = React.useState(false);
  const [isConfirmed, setConfirmation] = React.useState(false);
  const [data, setData] = React.useState(new PromotionModel());
  const [loader, setLoader] = React.useState(true);

  useEffect(() => {
    loadPromotionData();
  }, []);

  const loadPromotionData = async () => {
    setLoader(true);
    const response = await firstValueFrom(
      PromotionService.getPromotionData(
        AuthService.authData.accountId,
        promotionId || ""
      )
    );
    if (response?.result) {
      const data = PromotionModel.deserializeAPIModel(response.result);
      setData(data);
      setLoader(false);
    } else {
      ToastService.showError("Error in getting promotion details");
    }
    setLoader(false);
  };

  function onDelete() {
    if (isAPIRequestPending) return;

    setAPIRequestPending(true);
    PromotionService.deletePromotion(
      AuthService.authData.accountId,
      promotionId
    ).subscribe({
      next: (response) => {
        setAPIRequestPending(false);
        if (response.result) {
          ToastService.showSuccess("Promotion Deleted Successfully");
        }
        onClose(true);
      },
      error: () => {
        setAPIRequestPending(false);
        ToastService.showError("Error in deleting promotion");
      },
    });
  }

  const renderContent = () => {
    if (loader) {
      return <>{new Loader(true).loaderComponent}</>;
    }
    return (
      <FlexContainer fxLayout="column" spacing={30} alignItems="center">
        <CustomLabel>
          Do you want to <span className="red-text semi-bold">delete</span> this
          Promotion?
        </CustomLabel>
        <div>
          <FlexContainer fxLayout="column" spacing={10} alignItems="center">
            <CustomLabel textAlign="center" fontWeight="bold">
              {data.name}
            </CustomLabel>
            <CustomLabel textAlign="center">
              {data.lastUsed
                ? `Last Used ${UtilityMethods.formatDateInString(data.lastUsed)}`
                : "Never Used"}
            </CustomLabel>
          </FlexContainer>
        </div>
        <Input
          type="checkbox"
          label="Confirm Deletion"
          value={isConfirmed}
          onChange={(e) => setConfirmation(e.target.checked)}
        />
      </FlexContainer>
    );
  };

  return (
    <ModalComponent
      title={"Delete Promotion"}
      titleFontSize="22px"
      bodyPadding="30px 55px 20px 54px"
      onClose={() => onClose && onClose(false)}
      showFooter={true}
      wrapperWidth="600px"
      bodyBackgroundColor="rgba(255, 255, 255, 1)"
      footerBackground="rgba(255, 0, 0, 0.1)"
      footerHeight="auto"
      minHeight="200px"
      redHeader={true}
      blueHeader={false}
      footerContent={
        <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
          <RedButton
            lineHeight="13px"
            minWidth="120px"
            height="30px"
            onClick={() => onClose(false)}
          >
            Do not Delete
          </RedButton>
          <RedButton
            lineHeight="13px"
            primary
            disabled={isAPIRequestPending || !isConfirmed}
            minWidth="120px"
            height="30px"
            onClick={() => onDelete()}
          >
            Delete
          </RedButton>
        </FlexContainer>
      }
    >
      {renderContent()}
    </ModalComponent>
  );
};

export default withRouter(observer(DeletePromotionPopUp));
